import dayjs, { Dayjs } from 'dayjs';

export type TalentInvoiceTiming = {
  sentOn: Dayjs | null;
  dueDate: Dayjs | null;
  overdueDate: Dayjs | null;
  isOverdue: boolean;
  daysUntilDue: number | null;
};

/**
 * Shared device-local invoice clock: due date is unchanged, and reporting opens
 * at the start of the next calendar day for every valid payment term.
 */
export function getTalentInvoiceTiming(
  sentOnValue: string | Date | null | undefined,
  paymentTermsValue: number | string | null | undefined,
  now: Dayjs = dayjs(),
): TalentInvoiceTiming {
  const parsedSentOn = sentOnValue ? dayjs(sentOnValue) : null;
  const sentOn = parsedSentOn?.isValid() ? parsedSentOn : null;
  const parsedTerms = Number(paymentTermsValue);
  const validTerms = Number.isSafeInteger(parsedTerms) && parsedTerms > 0;
  const paymentTerms = validTerms ? parsedTerms : 14;
  const dueDate = sentOn ? sentOn.add(paymentTerms, 'day') : null;
  const overdueDate = dueDate?.isValid() && validTerms
    ? dueDate.startOf('day').add(1, 'day')
    : null;
  const daysUntilDue = dueDate ? dueDate.startOf('day').diff(now.startOf('day'), 'day') : null;

  return {
    sentOn,
    dueDate,
    overdueDate,
    isOverdue: overdueDate ? now.valueOf() >= overdueDate.valueOf() : false,
    daysUntilDue,
  };
}

export type ReportingInvoice = {
  id?: number;
  sentOn?: string | null;
  hygienistMarkedPaid?: boolean | number | string | null;
  nonPaymentReportedOn?: string | null;
  nonPaymentEscalatedOn?: string | null;
  officePaymentStatus?: string | null;
  talentPaymentStatus?: string | null;
};

export function getTalentNonPaymentState(
  invoice: ReportingInvoice | null | undefined,
  paymentTerms: number | string | null | undefined,
  isCentralBooked: number | string | null | undefined,
  now: Dayjs = dayjs(),
) {
  const timing = getTalentInvoiceTiming(invoice?.sentOn, paymentTerms, now);
  // T2-7494 (D11): report only while the temp's payment is UNPAID. Once the office has paid
  // (PAID, or ACTION REQUIRED with "Click here to resolve"), the API refuses a report too.
  const excluded = !invoice?.id || Number(isCentralBooked) === 2
    || Number(invoice?.hygienistMarkedPaid) > 0 || invoice?.talentPaymentStatus === 'paid'
    || invoice?.officePaymentStatus === 'paid';
  const reported = !!invoice?.nonPaymentReportedOn;
  const escalated = !!invoice?.nonPaymentEscalatedOn;
  const reportDate = invoice?.nonPaymentReportedOn ? dayjs(invoice.nonPaymentReportedOn) : null;
  const escalationDate = reportDate?.isValid() ? reportDate.startOf('day').add(1, 'day') : null;
  const receiptEndsRecourse = Number(invoice?.hygienistMarkedPaid) > 0 || invoice?.talentPaymentStatus === 'paid';
  return {
    timing,
    reported,
    escalated,
    escalationDate,
    canEscalate: !!invoice?.id && Number(isCentralBooked) !== 2 && reported && !escalated && !receiptEndsRecourse
      && !!escalationDate && now.valueOf() >= escalationDate.valueOf(),
    canReport: !excluded && !reported && timing.isOverdue,
    showGraceMessage: !excluded && !reported && !!timing.overdueDate && !timing.isOverdue,
    graceMessage: timing.dueDate && timing.overdueDate
      ? `This invoice is due ${timing.dueDate.format('MMM D, YYYY')}. You'll be able to report non-payment starting ${timing.overdueDate.format('MMM D, YYYY')} if it's still unpaid.`
      : null,
  };
}
